import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lock, KeyRound, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Wachtwoord moet minstens 6 tekens zijn."); return; }
    if (password !== confirm) { setError("De wachtwoorden komen niet overeen."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      applyAuth(data);
      toast.success("Je wachtwoord is bijgewerkt");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-12 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] glow-rose blur-3xl" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="font-serif text-3xl font-semibold text-zinc-100">FETSHOP</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-rose-400 mt-1.5">.nl</span>
        </Link>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/80 backdrop-blur p-8 noise-overlay">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-rose-400" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-rose-400">Nieuw wachtwoord</span>
          </div>
          <h1 className="font-serif text-3xl font-semibold text-zinc-100 mb-6">Wachtwoord instellen</h1>

          {!token ? (
            <div data-testid="reset-no-token" className="flex items-start gap-3 rounded-lg border border-amber-800/50 bg-amber-950/20 px-4 py-3 text-sm text-amber-300">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>Deze link is ongeldig. Vraag een nieuwe herstel-link aan via de inlogpagina.</span>
            </div>
          ) : (
            <>
              {error && (
                <div data-testid="reset-error" className="mb-4 rounded-lg border border-rose-900/50 bg-rose-950/30 px-4 py-2.5 text-sm text-rose-300">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <Field icon={KeyRound} type="password" placeholder="Nieuw wachtwoord" value={password} onChange={(e) => setPassword(e.target.value)} testid="reset-password-input" required />
                <Field icon={KeyRound} type="password" placeholder="Herhaal wachtwoord" value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="reset-confirm-input" required />
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? "Even geduld..." : "Wachtwoord opslaan"}
                  {!loading && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center text-sm text-zinc-500">
            <Link to="/auth" data-testid="reset-back-link" className="text-rose-400 hover:text-rose-300 font-medium">Terug naar inloggen</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
      <input
        data-testid={testid}
        {...props}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 pl-10 pr-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-700 transition-colors"
      />
    </div>
  );
}
